/**
 * Geometria do alargador Holmatro: o contrato cinemático e os contornos das peças.
 *
 * O contrato (pivô, ponto de ponta, ângulo de fechamento) é o que a cena usa
 * para encostar as pontas na lataria — `poseAlargadorElevacao` e a dobra da aba
 * calculam onde a ponta está a partir dele. Antes ele vivia copiado em três
 * arquivos como números soltos; agora vive aqui.
 *
 * Os contornos são desenhados **na pose fechada**, em coordenadas do
 * alargador: x ao longo da ferramenta (positivo para as pontas), y para cima.
 * Desenhar fechado é o que garante que, com `anguloMandibula = 0`, as faces
 * internas dos dois braços fiquem rentes ao eixo em todo o comprimento. O
 * modelo antigo era feito de caixas retas presas a pivôs separados por 18 cm:
 * as pontas se tocavam, mas os corpos formavam um "<" aberto, e o alargador
 * fechado parecia aberto.
 *
 * Tudo aqui é puro e testado sem WebGL. O braço inferior é o espelho do
 * superior em y.
 */

import { arco, curva, type Ponto2 } from '../componentes/contornos2d'

export type { Ponto2 }

/** Pivô do braço superior; o inferior fica em `[x, -y]`. */
export const PIVO_BRACO: Ponto2 = [0.06, 0.09]
/**
 * Ponto de contato da ponta, no referencial do braço (antes da rotação). É o
 * ponto que a cena encosta na lataria.
 */
export const PONTA_BRACO: Ponto2 = [0.46, 0.045]
/** Rotação do braço superior com `anguloMandibula = 0`. */
export const ANGULO_FECHADO = -0.28

/** Folga entre as faces internas fechadas: o bastante para não haver z-fighting. */
export const FOLGA_FECHADO = 0.003

/** Ponto de contato da ponta do braço superior, no referencial do alargador. */
export function pontaDoBraco(anguloMandibula: number): Ponto2 {
  const theta = ANGULO_FECHADO + anguloMandibula
  const [px, py] = PONTA_BRACO
  return [
    PIVO_BRACO[0] + px * Math.cos(theta) - py * Math.sin(theta),
    PIVO_BRACO[1] + px * Math.sin(theta) + py * Math.cos(theta),
  ]
}

/** Ponta do braço fechado: o bico, um pouco além do ponto de contato. */
const X_BICO = 0.55
/** Raio do calcanhar do braço em volta do pivô. */
const RAIO_CALCANHAR = 0.05

/**
 * Corpo forjado do braço superior: largo no pivô, afinando até o bico, com a
 * face interna reta sobre o eixo. O dorso é uma curva única — é ela que dá a
 * silhueta de "bico de pato" do alargador real, que as caixas não tinham.
 */
export function contornoBraco(passos = 10): Ponto2[] {
  const y0 = FOLGA_FECHADO
  const raizInterna: Ponto2 = [0.1, y0]
  const bico: Ponto2 = [X_BICO, y0 + 0.004]
  // o dorso encontra o calcanhar tangente ao arco, sem degrau
  const anguloOmbro = 0.5
  const ombro: Ponto2 = [
    PIVO_BRACO[0] + RAIO_CALCANHAR * Math.cos(anguloOmbro),
    PIVO_BRACO[1] + RAIO_CALCANHAR * Math.sin(anguloOmbro),
  ]
  const fimCalcanhar = Math.PI * 1.15
  return [
    raizInterna,
    [X_BICO - 0.02, y0],
    bico,
    // dorso, do bico ao ombro sobre o pivô
    ...curva(bico, [0.36, 0.1], ombro, passos),
    // calcanhar arredondado em volta do pivô, voltando para o eixo
    ...arco(PIVO_BRACO, RAIO_CALCANHAR, anguloOmbro, fimCalcanhar, passos),
    ...curva(
      [PIVO_BRACO[0] + RAIO_CALCANHAR * Math.cos(fimCalcanhar), PIVO_BRACO[1] + RAIO_CALCANHAR * Math.sin(fimCalcanhar)],
      [0.03, y0 + 0.01],
      raizInterna,
      Math.max(2, passos / 2),
    ),
  ]
}

/** Altura do dorso do braço em `x`, para posicionar o que vai sobre ele. */
export function dorsoEm(x: number, contorno: readonly Ponto2[] = contornoBraco(24)): number {
  let melhor = 0
  for (const [px, py] of contorno) {
    if (Math.abs(px - x) < 0.02 && py > melhor) melhor = py
  }
  return melhor
}

/**
 * Ponteira dentada destacável (grafite), cobrindo o terço final do braço. O
 * dorso é serrilhado em dentes inclinados para trás, como os da ponteira de
 * garra: inclinados assim eles mordem a chapa quando o braço abre e soltam
 * quando fecha.
 */
export function contornoPonteira(dentes = 5): Ponto2[] {
  const y0 = FOLGA_FECHADO
  const inicio = 0.4
  const bico: Ponto2 = [X_BICO + 0.008, y0 + 0.004]
  const base: Ponto2 = [inicio, dorsoEm(inicio) + 0.004]
  const pontos: Ponto2[] = [[inicio, y0], [X_BICO - 0.02, y0], bico]
  // do bico até a base, alternando vale (na linha do dorso) e crista (fora dela)
  const dx = base[0] - bico[0]
  const dy = base[1] - bico[1]
  const comprimento = Math.hypot(dx, dy)
  const normal: Ponto2 = [-dy / comprimento, dx / comprimento]
  // a normal precisa apontar para fora (y positivo) no braço superior
  const sentido = normal[1] >= 0 ? 1 : -1
  for (let i = 0; i < dentes; i++) {
    const t0 = (i + 0.15) / dentes
    const crista = (i + 0.85) / dentes
    const vale: Ponto2 = [bico[0] + dx * t0, bico[1] + dy * t0]
    const alto: Ponto2 = [
      bico[0] + dx * crista + sentido * normal[0] * 0.009,
      bico[1] + dy * crista + sentido * normal[1] * 0.009,
    ]
    pontos.push(vale, alto)
  }
  pontos.push(base)
  return pontos
}

/**
 * Bolsa usinada de alívio de peso na face do braço: um losango alongado
 * dentro do corpo, longe das bordas e da ponteira.
 */
export function contornoBolsa(): Ponto2[] {
  return [
    [0.15, 0.03],
    [0.36, 0.024],
    [0.37, 0.04],
    [0.26, 0.072],
    [0.16, 0.096],
    [0.135, 0.07],
  ]
}

/**
 * Placa lateral do garfo (yoke): envolve os dois pivôs e desce até o cilindro.
 * Simétrica em y; os braços ficam entre as duas placas.
 */
export function contornoGarfo(passos = 8): Ponto2[] {
  const [px, py] = PIVO_BRACO
  const raio = 0.05
  return [
    [-0.07, -0.06],
    ...arco([px, -py], raio, Math.PI * 1.25, Math.PI * 2.1, passos),
    [px + raio * 0.95, 0],
    ...arco([px, py], raio, -Math.PI * 0.1, Math.PI * 0.75, passos),
    [-0.07, 0.06],
  ]
}
